import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Badge, Button, Spinner, Input } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { getInvoice, describeError, formatInr } from './purchasesApi';

const PurchaseInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getInvoice(id)
      .then(setData)
      .catch((e) => setError(describeError(e, 'Could not load that invoice.')))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center p-5"><Spinner /></div>;
  if (error) return <Container fluid><Card><CardBody className="text-center p-5 text-danger">{error}</CardBody></Card></Container>;

  const { invoice, items } = data;
  const q = search.trim().toLowerCase();
  const shown = items.filter((i) => !q ||
    [i.style_sku, i.size_sku, i.description, i.product_title, i.colour]
      .some((f) => String(f ?? '').toLowerCase().includes(q)));

  return (
    <Fragment>
      <Breadcrumbs parent="Purchases" title={invoice.pi_ref} mainTitle={`${invoice.pi_ref} — ${invoice.pi_date}`} />
      <Container fluid>
        <Row><Col sm="12"><Card>
          <CardHeader className="d-flex justify-content-between align-items-center py-3">
            <div>
              <h5 className="mb-0">{invoice.pi_ref}</h5>
              <small className="text-muted">
                {invoice.pi_date} · {invoice.supplier} · {items.length} lines · {invoice.currency}
              </small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Input type="search" value={search} placeholder="Search this invoice"
                     onChange={(e) => setSearch(e.target.value)} style={{ width: 'auto', minWidth: 220 }} />
              <Button color="secondary" outline onClick={() => navigate('/purchases/invoices')}>Back</Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="table-responsive">
              <Table className="table-bordered" size="sm">
                <thead>
                  <tr>
                    <th>Product</th><th>SKU</th><th>Size</th><th>Colour</th>
                    <th className="text-end">Cost</th>
                    {/* What the same item cost on the PI before this one — the reason
                        to open a single invoice at all. */}
                    <th className="text-end">Previous</th><th className="text-end">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr><td colSpan="7" className="text-center text-muted p-3">Nothing matches this search.</td></tr>
                  ) : shown.map((it) => (
                    <tr key={it.id}>
                      <td>
                        {it.product_title || it.description || <span className="text-muted">—</span>}
                        {!it.product_id && <Badge color="warning" pill className="ms-2">not in catalogue</Badge>}
                      </td>
                      <td><small>{it.style_sku}</small></td>
                      <td><small className="text-muted">{it.size_label || it.size_sku}</small></td>
                      <td><small className="text-muted">{it.colour || '—'}</small></td>
                      <td className="text-end"><strong>{formatInr(it.cost)}</strong></td>
                      <td className="text-end">
                        {it.prev_cost ? <>{formatInr(it.prev_cost)} <small className="text-muted d-block">{it.prev_pi_ref}</small></> : <span className="text-muted">first PI</span>}
                      </td>
                      <td className="text-end">
                        {it.delta_pct === null || it.delta_pct === undefined ? <span className="text-muted">—</span>
                          : Number(it.delta_pct) === 0 ? <Badge color="secondary" pill>no change</Badge>
                          : <Badge color={Number(it.delta_pct) > 0 ? 'danger' : 'success'} pill>
                              {Number(it.delta_pct) > 0 ? '+' : ''}{it.delta_pct}%
                            </Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card></Col></Row>
      </Container>
    </Fragment>
  );
};

export default PurchaseInvoiceDetail;
